import { Component, DestroyRef, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from '@angular/router';
import {
  debounceTime,
  delay,
  filter,
  map,
  switchMap,
  take
} from 'rxjs/operators';
import { combineLatest, Subject, Subscription, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { APP_ROUTES } from './core-module/constants/routes';
import { environment } from '../environments/environment';

import {
  GoogleAnalyticsService,
  RouteStateService,
  SpinnerService,
  CookieConsentService
} from './core-module/services';
import { PosthogService } from './core-module/services/posthog/posthog.service';
import { UiTrackerService } from './core-module/services/ui-tracker/ui-tracker.service';
import { RegistrationDraftFlushService } from './core-module/services/auth/registration-draft-flush.service';
import { MagicLinkLandingService } from './core-module/services/auth/magic-link-landing.service';
import { AccountCreationPromptService } from './core-module/services/auth/account-creation-prompt.service';
import { RobotsMetaService } from './core-module/services/seo/robots-meta.service';
import { manageRouteFocus } from './core-module/services/route-focus/route-focus';

import { DeviceFacade } from './state/device/device.facade';
import { TrackerFacade } from './state/tracker';
import { UserProfileFacade } from './state/user-profile';
import { MaintanceFacade } from './state/maintance';
import { AiChatFacade } from './state/ai-chat';
import { ReturnSessionBannerComponent } from '@cpd-app/core-module/components/return-session-banner/return-session-banner.component';
import { AuthFacade } from '@cpd-app/state/auth';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'cpd-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  public readonly routes = APP_ROUTES;

  // Route-section state, previously read from empty routerLinkActive anchors.
  // See RouteStateService for why those had to go.
  public readonly isErrorRoute = this.routeState.isErrorRoute;
  public readonly isAboutRoute = this.routeState.isAboutRoute;
  public readonly isManagerRoute = this.routeState.isManagerRoute;
  public readonly isAnalyticsRoute = this.routeState.isAnalyticsRoute;
  public readonly isAccountRoute = this.routeState.isAccountRoute;
  public readonly isPublicPageRoute = this.routeState.isPublicPageRoute;
  public readonly isSharedReportRoute = this.routeState.isSharedReportRoute;

  public isLoading$ = this.getLoadingState();
  public isOpenChatVia$ = this.aiChatFacade.isOpenChatVia$;
  public isMobile$ = this.deviceFacade.isMobile$;
  /**
   * MOBILE.1 R2. Under 960px the bottom bar (in cpd-header) takes over from
   * both the rail and the floating Via button. Read by width, where isMobile$
   * is a user-agent test.
   */
  public isNarrow$ = this.deviceFacade.isLessThanMedium$;

  private readonly resize$ = new Subject<DOMRect>();
  private navigationSpinnerTimer: Subscription | null = null;

  constructor(
    private readonly router: Router,
    private readonly deviceFacade: DeviceFacade,
    private readonly spinnerService: SpinnerService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly posthogService: PosthogService,
    private readonly userProfileFacade: UserProfileFacade,
    private readonly trackerFacade: TrackerFacade,
    private readonly maintanceFacade: MaintanceFacade,
    private readonly destroyRef: DestroyRef,
    private readonly aiChatFacade: AiChatFacade,
    private readonly snackBar: MatSnackBar,
    private readonly authFacade: AuthFacade,
    private readonly routeState: RouteStateService,
    private readonly registrationDraftFlush: RegistrationDraftFlushService,
    private readonly cookieConsent: CookieConsentService,
    private readonly robotsMeta: RobotsMetaService,
    private readonly dialog: MatDialog,
    // ANALYTICS.25. Trailing and optional so constructor-built specs that
    // predate it keep compiling; Angular still injects it.
    private readonly uiTracker?: UiTrackerService,
    // AUTH.7. Trailing and optional for the same reason.
    private readonly magicLinkLanding?: MagicLinkLandingService,
    private readonly accountPrompt?: AccountCreationPromptService
  ) {}

  public ngOnInit(): void {
    this.initializeServices();
    this.setupRouteFocusManagement();
    this.setupRouterEvents();
    this.setupNavigationSpinner();
    this.removeUnthemedClasses();

    this.setupUserProfileTracking();
    this.setupResizeHandling();

    // Registration collects branch, installation and separation before the
    // account exists, because sign-up is a magic link. This writes them to the
    // profile on the first load after the member opens that link, and is a
    // no-op when there is no draft held.
    this.registrationDraftFlush.flushWhenAuthenticated();

    // The consent banner used to be started by BuildYourTargetComponent,
    // which was the landing page. Now that the root redirects to /home, that
    // component may never mount, so nothing asked for consent and Google
    // Analytics (gated on that consent) never activated. The shell owns it.
    this.cookieConsent.embedCookies();

    this.setupMagicLinkContinue();
  }

  /**
   * AUTH.7. A page opened from an emailed sign-in link asks the person to
   * press "Continue to sign in" rather than spending the link on load, so a
   * mail scanner opening the link first no longer uses it up.
   */
  private setupMagicLinkContinue(): void {
    this.magicLinkLanding?.offer$
      .pipe(
        filter((link) => !!link),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const link = this.magicLinkLanding.consumeOffer();
        if (link) {
          this.accountPrompt?.openMagicLinkContinue(link);
        }
      });
  }

  public onResized(event: Element): void {
    const rectangle = event.getBoundingClientRect();
    this.resize$.next(rectangle);
  }

  /** Focus to <main> after each new page; see manageRouteFocus. */
  private setupRouteFocusManagement(): void {
    manageRouteFocus(this.router, this.destroyRef);
  }

  private getLoadingState() {
    return combineLatest([
      this.maintanceFacade.isLoading$,
      this.spinnerService.isSpinningStream$,
      this.spinnerService.isNavigatingStream$
    ]).pipe(
      map(
        ([maintenanceLoading, httpLoading, navigating]) =>
          maintenanceLoading || httpLoading || navigating
      ),
      delay(0)
    );
  }

  /**
   * Shows the global spinner during router navigations. Route resolvers (e.g.
   * the occupation-detail page) issue GraphQL requests that the
   * HTTP-interceptor spinner ignores, because the GraphQL endpoint is not under
   * `apiUrlBase`. Without this, a slow resolver leaves the user staring at an
   * unchanged page with no feedback, which is what drives the "dead click /
   * over-click" behavior. The short delay before showing keeps fast
   * navigations from flashing the overlay.
   */
  private setupNavigationSpinner(): void {
    /*
     * CPD-658. A dialog and this spinner must never be up together.
     *
     * The spinner's scrim is z-index 1100 and Angular Material's
     * `.cdk-overlay-container` is 1000, so the spinner renders OVER any open
     * dialog and swallows its clicks. Where the dialog is the thing the
     * navigation is waiting on, that deadlocks: the goals report's
     * `canDeactivate` (goalsReportLoginPromptGuard) suspends navigation on a
     * login dialog's `afterClosed()` for anonymous members -- signed-in
     * members return `of(true)` and never see it, which is exactly the
     * asymmetry QA reported. An unreachable dialog never closes, so no
     * NavigationEnd/Cancel/Error ever arrives and the hide branch below never
     * runs. The member is left under a spinner that outlives the page.
     *
     * Lowering the spinner under 1000 would fix the whole class, but it would
     * also stop the spinner covering the masthead on every load in the app --
     * a visible change to a working surface, so it wants its own ticket and
     * its own before/after review.
     *
     * A spinner means "the app is working". While a dialog is open the app is
     * waiting on the member, so there is nothing to report and suppressing it
     * is right on its own terms, not only here.
     */
    this.dialog.afterOpened
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearNavigationSpinner());

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.navigationSpinnerTimer?.unsubscribe();
          this.navigationSpinnerTimer = timer(150).subscribe(() => {
            // Covers the other ordering: the guard opened its dialog before
            // this timer fired, so there is no afterOpened left to wait for.
            if (this.dialog.openDialogs.length > 0) {
              return;
            }
            this.spinnerService.showNavigationSpinner();
          });
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          this.clearNavigationSpinner();
        }
      });
  }

  /** Cancels a pending show and hides anything already showing. */
  private clearNavigationSpinner(): void {
    this.navigationSpinnerTimer?.unsubscribe();
    this.navigationSpinnerTimer = null;
    this.spinnerService.hideNavigationSpinner();
  }

  private initializeServices(): void {
    // S-1. Emits `noindex, nofollow` on the two unauthenticated routes that
    // carry a real member's name. Runs in every environment, including LOCAL:
    // it is a correctness behaviour of those pages, not telemetry.
    this.robotsMeta.init();

    // ANALYTICS.25. Our own first-party usage capture, so it runs in every
    // environment, dev and staging included: it writes to our database, not
    // to a third party, and dev is where it has to be proven.
    this.uiTracker?.start();

    if (environment.envName !== 'LOCAL') {
      this.posthogService.init();
      this.googleAnalyticsService.initialize();
    }
  }

  private setupRouterEvents(): void {
    if (environment.envName !== 'LOCAL') {
      this.router.events
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd
          )
        )
        .subscribe((event) => {
          this.posthogService.capturePageview({
            url: event.urlAfterRedirects,
            path: event.urlAfterRedirects.split('?')[0]
          });
          this.googleAnalyticsService.emitEvent(event.urlAfterRedirects);
        });
    }
  }

  private setupUserProfileTracking(): void {
    this.userProfileFacade.userProfile$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((user) => !!user?.userId),
        take(1)
      )
      .subscribe((user) => {
        // Identify user in PostHog
        this.posthogService.identify(user.userId.toString(), {
          firstName: user.firstName,
          lastName: user.lastName,
          profileWorkType: user.profileWorkType,
          environment: environment.envName
        });

        // Track video usage
        this.trackerFacade.saveLocalVideoUsage();
      });

    combineLatest([
      this.authFacade.isSessionInitializing$,
      this.authFacade.authToken$,
      this.authFacade.shareToken$
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          ([isInit, shareToken, authToken]) =>
            !!shareToken && !isInit && !!authToken
        )
      )
      .subscribe({
        next: () => this.userProfileFacade.loadShareUserProfile()
      });

    this.userProfileFacade.userShareProfile$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((shareProfile) => !!shareProfile),
        switchMap(() =>
          this.snackBar
            .openFromComponent(ReturnSessionBannerComponent, {
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'remind'
            })
            .afterDismissed()
        )
      )
      .subscribe();
  }
  private setupResizeHandling(): void {
    this.resize$
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(1000))
      .subscribe((value) => {
        this.deviceFacade.setDeviceWidthHeight(value.width, value.height);
      });
  }

  private removeUnthemedClasses(): void {
    Array.from(document.querySelectorAll('.mat-unthemed')).forEach((el) =>
      el.classList.remove('mat-unthemed')
    );
  }
}
